// Shared Academy Portal redesign primitives — ported from
// academy_portal_redesign/ap-ui.jsx into real components: Heroicons instead
// of the prototype's own icon set, and every rebuilt screen (Calendar, Home,
// Feed, Messages, Groups) imports from here instead of re-implementing its
// own card/pill/sheet markup, same as the prototype's component list implies.
// Must be rendered under the .ap-shell class (src/styles/academyPortal.css)
// so the CSS custom properties these reference resolve.
import React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAcademyShellRoot } from '../AcademyThemeVars';

const initialsOf = (name = '') => name.trim().split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();

export function Avatar({ name, s = 36, tone = 'accent', ring }) {
  const tones = {
    accent: { bg: 'var(--accent-light)', fg: 'var(--accent-ink)' },
    navy: { bg: 'var(--navy-2)', fg: '#fff' },
    neutral: { bg: 'var(--sunken)', fg: 'var(--t2)' },
    solid: { bg: 'var(--accent)', fg: '#fff' },
  }[tone];
  return (
    <div style={{
      width: s, height: s, borderRadius: '50%', background: tones.bg, color: tones.fg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.max(9.5, s * 0.36), fontWeight: 700, letterSpacing: '-0.01em',
      boxShadow: ring ? '0 0 0 2px var(--surface)' : 'none',
    }}>{initialsOf(name)}</div>
  );
}

export function AvatarStack({ names = [], s = 24, max = 4, extra }) {
  const shown = names.slice(0, max);
  const rest = (extra != null ? extra : names.length) - shown.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((n, i) => (
        <div key={i} style={{ marginLeft: i ? -8 : 0 }}><Avatar name={n} s={s} ring /></div>
      ))}
      {rest > 0 && (
        <div style={{
          marginLeft: -8, width: s, height: s, borderRadius: '50%', background: 'var(--sunken)',
          border: '1px solid var(--border)', boxShadow: '0 0 0 2px var(--surface)', color: 'var(--t2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: s * 0.36, fontWeight: 700,
        }}>+{rest}</div>
      )}
    </div>
  );
}

// color/textColor come from the caller (src/utils/eventCategories.js's
// CATEGORY_COLORS) — this component is a pure presenter, it doesn't look
// categories up itself, so it has no dependency on any one screen's data.
export function CatChip({ label, color, textColor, size = 'sm' }) {
  const lg = size === 'lg';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, background: `color-mix(in oklab, ${color} 12%, white)`,
      color: textColor, fontSize: lg ? 11.5 : 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
      padding: lg ? '5px 11px' : '3px 8px', borderRadius: 20, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: lg ? 7 : 6, height: lg ? 7 : 6, borderRadius: 2, background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

export function Tag({ children, tone = 'neutral', style }) {
  const tones = {
    neutral: { bg: 'var(--sunken)', fg: 'var(--t2)', bd: 'var(--border)' },
    accent: { bg: 'var(--accent-light)', fg: 'var(--accent-ink)', bd: 'transparent' },
    navy: { bg: 'var(--navy)', fg: '#fff', bd: 'transparent' },
    warn: { bg: '#fef9c3', fg: '#854d0e', bd: 'transparent' },
  }[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, background: tones.bg, color: tones.fg,
      border: `1px solid ${tones.bd}`, fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em',
      textTransform: 'uppercase', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', ...(style || {}),
    }}>{children}</span>
  );
}

// `icon` takes a Heroicon component (e.g. PlusIcon), not the prototype's
// icon-name string. `type`/`disabled` are additions the prototype didn't
// need (its buttons were never wired to a real async submit) but every
// create/edit form in this app is.
export function Btn({
  children, icon: IconComp, variant = 'primary', size = 'md', full, onClick, style, danger, disabled, type = 'button',
}) {
  const pad = size === 'sm' ? '7px 12px' : size === 'lg' ? '12px 18px' : '9px 15px';
  const fs = size === 'sm' ? 12.5 : size === 'lg' ? 14.5 : 13.5;
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: pad,
    fontSize: fs, fontWeight: 600, letterSpacing: '-0.01em', borderRadius: 'var(--r-btn)',
    width: full ? '100%' : undefined, lineHeight: 1.2, opacity: disabled ? 0.5 : 1,
  };
  const variants = {
    primary: { background: danger ? '#b91c1c' : 'var(--accent)', color: '#fff', boxShadow: 'var(--sh-1)' },
    ghost: { background: 'var(--surface)', color: danger ? '#b91c1c' : 'var(--t1)', border: '1px solid var(--border)' },
    quiet: { background: 'transparent', color: danger ? '#b91c1c' : 'var(--t2)' },
    tint: { background: 'var(--accent-light)', color: 'var(--accent-ink)' },
    dark: { background: 'rgba(255,255,255,.12)', color: '#fff' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} className="ap-tap"
      style={{ ...base, ...variants[variant], ...(style || {}) }}>
      {IconComp && <IconComp style={{ width: size === 'sm' ? 14 : 16, height: size === 'sm' ? 14 : 16 }} />}
      {children}
    </button>
  );
}

export function IconBtn({ icon: IconComp, onClick, tone = 'light', s = 34, badge, label, style, type = 'button' }) {
  const tones = {
    light: { bg: 'var(--surface)', fg: 'var(--t2)', bd: '1px solid var(--border)' },
    dark: { bg: 'rgba(255,255,255,.11)', fg: '#fff', bd: '1px solid rgba(255,255,255,.07)' },
    bare: { bg: 'transparent', fg: 'var(--t3)', bd: 'none' },
  }[tone];
  const iconSize = s > 32 ? 17 : 15;
  return (
    <button type={type} onClick={onClick} aria-label={label} className="ap-tap" style={{
      width: s, height: s, borderRadius: 9, background: tones.bg, color: tones.fg, border: tones.bd,
      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0, ...(style || {}),
    }}>
      <IconComp style={{ width: iconSize, height: iconSize, strokeWidth: 1.8 }} />
      {badge ? <span style={{
        position: 'absolute', top: -2, right: -2, minWidth: 15, height: 15, padding: '0 3px', borderRadius: 8,
        background: 'var(--accent)', color: '#fff', fontSize: 9.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px var(--navy)',
      }}>{badge}</span> : null}
    </button>
  );
}

// options: array of plain values, or { v, l } pairs when the label should
// differ from the value (e.g. a translated label over an English-canonical
// value used elsewhere in the app).
export function Segmented({ value, options, onChange, full, size = 'md' }) {
  return (
    <div style={{
      display: 'inline-flex', background: 'var(--sunken)', border: '1px solid var(--border)',
      borderRadius: 10, padding: 3, gap: 2, width: full ? '100%' : undefined,
    }}>
      {options.map((o) => {
        const v = o?.v !== undefined ? o.v : o;
        const active = v === value;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} className="ap-tap" style={{
            flex: full ? 1 : undefined, padding: size === 'sm' ? '5px 10px' : '7px 13px', borderRadius: 7,
            fontSize: size === 'sm' ? 12 : 12.5, fontWeight: active ? 700 : 500, letterSpacing: '-0.01em',
            background: active ? 'var(--surface)' : 'transparent', color: active ? 'var(--t1)' : 'var(--t3)',
            boxShadow: active ? '0 1px 3px rgba(16,24,40,.1)' : 'none', whiteSpace: 'nowrap',
          }}>{o?.l || v}</button>
        );
      })}
    </div>
  );
}

export function FilterBtn({ label, value, onClick, active }) {
  return (
    <button type="button" onClick={onClick} className="ap-tap" style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 9,
      background: active ? 'var(--accent-light)' : 'var(--surface)',
      border: `1px solid ${active ? 'transparent' : 'var(--border)'}`, minWidth: 0,
    }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: active ? 'var(--accent-ink)' : 'var(--t3)' }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: active ? 'var(--accent-ink)' : 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{value}</span>
      <ChevronDownIcon style={{ width: 13, height: 13, strokeWidth: 2, color: active ? 'var(--accent-ink)' : 'var(--t3)', flexShrink: 0 }} />
    </button>
  );
}

// Bottom sheet on phone (< 1024px), centred card on desktop — a single
// responsive layout via Tailwind's `lg:` breakpoint (this app's one
// mobile/desktop breakpoint, per the redesign spec) rather than a JS mode
// prop, matching how the existing hand-rolled modals in this codebase
// already do `items-end sm:items-center` etc.
export function Sheet({ open, onClose, title, sub, children, footer, maxH = '86%' }) {
  const shellRoot = useAcademyShellRoot();
  if (!open) return null;
  const sheet = (
    <div
      onClick={onClose}
      className="ap-sheet-backdrop fixed inset-0 z-50 flex"
      style={{ background: 'rgba(11,17,29,.45)', backdropFilter: 'blur(2px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ap-sheet-panel flex flex-col overflow-hidden"
        style={{
          '--ap-sheet-max-h': maxH,
          background: 'var(--surface)', boxShadow: 'var(--sh-3)', paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
        }}
      >
        <div className="ap-sheet-grabber flex justify-center pt-2">
          <div style={{ width: 38, height: 4, borderRadius: 3, background: 'var(--border-2)' }} />
        </div>
        {(title || sub) && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px 12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--t1)' }}>{title}</h3>}
              {sub && <p style={{ fontSize: 12.5, color: 'var(--t3)', marginTop: 3, lineHeight: 1.45 }}>{sub}</p>}
            </div>
            <IconBtn icon={XMarkIcon} onClick={onClose} tone="bare" s={28} label="Close" />
          </div>
        )}
        <div className="ap-hide-sb" style={{ overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0, padding: '0 18px 4px' }}>{children}</div>
        {footer && <div style={{ padding: '12px 18px 0', borderTop: '1px solid var(--border)', marginTop: 12, flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>
  );
  // See useAcademyShellRoot — escapes any ancestor stacking context.
  return shellRoot ? createPortal(sheet, shellRoot) : sheet;
}

export function SectionHead({ children, action, onAction, style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 10, ...(style || {}) }}>
      <h2 className="ap-ovl">{children}</h2>
      {action && <button type="button" onClick={onAction} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-ink)' }}>{action}</button>}
    </div>
  );
}

export function PageHead({ title, sub, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--t1)', letterSpacing: '-0.03em' }}>{title}</h1>
        {sub && <p style={{ fontSize: 13, color: 'var(--t3)', marginTop: 4, lineHeight: 1.4 }}>{sub}</p>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{children}</div>
    </div>
  );
}
